import { useState, useCallback, useRef, useEffect } from 'react';
import { getAccountId, getUniId } from '@/core/utils/extractCookieId';

interface CacheEntry<T> {
  data: T[];
  timestamp: number;
}

const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

/**
 * Generates cache key scoped per account/universe.
 * This prevents cache leaking between different accounts/universes.
 */
const getCacheKey = (): string => {
  const accountId = getAccountId();
  const universeId = getUniId();
  return `warroom_saved_filters_cache:${accountId}:${universeId}`;
};

/**
 * Reads cache from localStorage
 */
const readFromCache = <T,>(key: string): T[] | null => {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const { data, timestamp }: CacheEntry<T> = JSON.parse(cached);
    const now = Date.now();

    // Check if cache is still valid (within 1 hour)
    if (now - timestamp > CACHE_DURATION) {
      localStorage.removeItem(key);
      return null;
    }

    return data;
  } catch {
    return null;
  }
};

/**
 * Writes cache to localStorage
 */
const writeToCache = <T,>(key: string, data: T[]): void => {
  try {
    const cache: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(cache));
  } catch (error) {
    console.error('Failed to write cache to localStorage:', error);
  }
};

/**
 * Custom hook for managing saved filters with localStorage cache.
 * 
 * **Cache is automatically scoped per account/universe** to prevent data leaking
 * between different accounts when switching users.
 * 
 * **Automatic fetch behavior:**
 * - On mount, if cache is empty or expired, automatically fetches from API
 * - All subsequent reads come from the cache (no API calls)
 * 
 * **Manual refresh (after save/delete/edit):**
 * 1. Call `invalidateCache()` to clear the cache
 * 2. Call `fetchFilters(true)` to force a fresh fetch from API
 * 
 * Cache expires after 1 hour and can be invalidated manually.
 * 
 * @param fetchFunction - Function that fetches the filters from the API
 * @returns Object with filters, loading state, fetch function, and cache invalidation
 * 
 * @example
 * ```typescript
 * const { filters, isLoading, fetchFilters, invalidateCache } = useSavedFiltersCache(
 *   () => SavedFiltersService.getSavedFilters()
 * );
 * 
 * // Filters are automatically loaded on mount from cache or API
 * 
 * // Force refresh after save/delete/edit
 * invalidateCache();
 * await fetchFilters(true);
 * ```
 */
export const useSavedFiltersCache = <T = any>(
  fetchFunction: () => Promise<T[]>
) => {
  const cacheKey = getCacheKey();
  
  const [filters, setFilters] = useState<T[]>(() => {
    // Try to read from cache on mount
    const cached = readFromCache<T>(cacheKey);
    return cached ?? [];
  });
  const [isLoading, setIsLoading] = useState(false);
  const hasFetchedRef = useRef(false);

  // Use ref to avoid recreating fetchFilters callback when fetchFunction changes
  const fetchFunctionRef = useRef(fetchFunction);
  useEffect(() => {
    fetchFunctionRef.current = fetchFunction;
  }, [fetchFunction]);

  /**
   * Checks if the current cache is still valid (less than 1 hour old)
   */
  const isCacheValid = useCallback((): boolean => {
    const cached = readFromCache<T>(cacheKey);
    return cached !== null;
  }, [cacheKey]);

  /**
   * Invalidates the cache.
   * Call this after save/delete/edit operations to ensure fresh data on next fetch.
   */
  const invalidateCache = useCallback(() => {
    try {
      localStorage.removeItem(cacheKey);
    } catch (error) {
      console.error('Failed to invalidate cache:', error);
    }
  }, [cacheKey]);

  /**
   * Fetches filters from cache or API.
   * 
   * @param forceRefresh - If true, bypasses cache and fetches from API
   * @returns Promise with the filters array
   */
  const fetchFilters = useCallback(async (forceRefresh = false): Promise<T[]> => {
    setIsLoading(true);
    
    try {
      // Try to use cache if not forcing refresh
      if (!forceRefresh) {
        const cached = readFromCache<T>(cacheKey);
        if (cached) {
          setFilters(cached);
          setIsLoading(false);
          return cached;
        }
      }

      // Fetch from API using the latest fetchFunction from ref
      const data = await fetchFunctionRef.current();
      
      // Update state and cache
      setFilters(data);
      writeToCache<T>(cacheKey, data);
      
      return data;
    } catch (error) {
      console.error('Failed to fetch filters:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [cacheKey]);

  // Auto-fetch on mount ONLY if cache is invalid/expired
  // Uses hasFetchedRef to prevent duplicate calls in React Strict Mode (development)
  useEffect(() => {
    // Prevent duplicate fetches (React Strict Mode mounts twice in dev)
    if (hasFetchedRef.current) {
      return;
    }
    
    // Check if cache exists and is valid
    const cachedData = readFromCache<T>(cacheKey);
    if (cachedData !== null) {
      // Cache is valid - use it, no need to fetch
      setFilters(cachedData);
      hasFetchedRef.current = true;
      return;
    }
    
    // Cache is invalid or doesn't exist - fetch from API
    hasFetchedRef.current = true;
    fetchFilters().catch(() => {
      // Errors are already logged in fetchFilters
    });
  }, [cacheKey, fetchFilters]);

  return { 
    filters, 
    isLoading, 
    fetchFilters, 
    invalidateCache,
    isCacheValid 
  };
};
